import { useState } from 'react'
import { X, Download, FileText, Image, Code } from 'lucide-react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { reportContent } from '@/data/reportContent'
import Button from '@/components/Button'

const ExportModal = ({ isOpen, onClose }) => {
  const [isExporting, setIsExporting] = useState(false)
  const [exportFormat, setExportFormat] = useState('pdf')

  if (!isOpen) return null

  const exportOptions = [
    { id: 'pdf', label: 'PDF Document', icon: FileText, description: 'Complete guide as PDF' },
    { id: 'md', label: 'Markdown File', icon: Code, description: 'Original markdown format' },
    { id: 'png', label: 'PNG Image', icon: Image, description: 'Visual summary' },
  ]

  const handleExport = async () => {
    setIsExporting(true)
    
    try {
      switch (exportFormat) {
        case 'pdf':
          await exportToPDF()
          break
        case 'md':
          exportToMarkdown()
          break
        case 'png':
          await exportToPNG()
          break
      }
    } catch (error) {
      console.error('Export failed:', error)
      alert('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
      onClose()
    }
  }

  const exportToPDF = async () => {
    const pdf = new jsPDF('p', 'mm', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 20
    
    // Add title
    pdf.setFontSize(24)
    pdf.setFont('helvetica', 'bold')
    pdf.text('GitHub Copilot Agent Complete Guide', margin, margin + 10)
    
    // Add subtitle
    pdf.setFontSize(12)
    pdf.setFont('helvetica', 'normal')
    pdf.text('The Complete Jumpstart Guide for AI-Powered Development', margin, margin + 20)
    
    // Add content
    const lines = reportContent.split('\n').filter(line => line.trim())
    let yPosition = margin + 35
    
    lines.forEach((line) => {
      if (yPosition > pageHeight - margin) {
        pdf.addPage()
        yPosition = margin
      }
      
      if (line.startsWith('# ')) {
        pdf.setFontSize(16)
        pdf.setFont('helvetica', 'bold')
        pdf.text(line.substring(2), margin, yPosition)
        yPosition += 10
      } else if (line.startsWith('## ')) {
        pdf.setFontSize(14)
        pdf.setFont('helvetica', 'bold')
        pdf.text(line.substring(3), margin, yPosition)
        yPosition += 8
      } else if (line.startsWith('### ')) {
        pdf.setFontSize(12)
        pdf.setFont('helvetica', 'bold')
        pdf.text(line.substring(4), margin, yPosition)
        yPosition += 6
      } else if (line.trim()) {
        pdf.setFontSize(10)
        pdf.setFont('helvetica', 'normal')
        const splitText = pdf.splitTextToSize(line, pageWidth - 2 * margin)
        pdf.text(splitText, margin, yPosition)
        yPosition += splitText.length * 4
      }
      yPosition += 2
    })
    
    pdf.save('github-copilot-agent-guide.pdf')
  }

  const exportToMarkdown = () => {
    const blob = new Blob([reportContent], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'github-copilot-agent-guide.md'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const exportToPNG = async () => {
    const element = document.getElementById('main-content') || document.body
    const canvas = await html2canvas(element, {
      height: window.innerHeight,
      width: window.innerWidth,
      useCORS: true,
    })
    
    const link = document.createElement('a')
    link.download = 'github-copilot-agent-guide.png'
    link.href = canvas.toDataURL()
    link.click()
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Export Guide
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4 mb-6">
            {exportOptions.map((option) => (
              <label
                key={option.id}
                className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                  exportFormat === option.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <input
                  type="radio"
                  name="export-format"
                  value={option.id}
                  checked={exportFormat === option.id}
                  onChange={(e) => setExportFormat(e.target.value)}
                  className="sr-only"
                />
                <option.icon className="w-5 h-5 text-primary-500 mr-3" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {option.label}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {option.description}
                  </div>
                </div>
              </label>
            ))}
          </div>

          <div className="flex space-x-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={isExporting}
              variant="primary"
              className="flex-1"
            >
              {isExporting ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span>{isExporting ? 'Exporting...' : 'Export'}</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExportModal
